// @ts-nocheck
import React, { useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'

import Grid from '@material-ui/core/Grid'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'

import TypographyPro from 'themes/TypographyPro'
import { mediaQueriesBreakpoints, respondAbove } from 'styles/mixins'
import UserPersonalInfo from './UserPersonalInfo'
import oStorage from 'utils/o-storage'
import { getECommerceDomainByEnv, getTranslatedTextByKey, sendRequest } from 'utils/utils'
import CTAText from 'components/common/CTAText'
import ButtonBase from 'components/common/ButtonBase'
import { useRouter } from 'next/router'

import type _Account from 'mobx/Account'
import type _Infra from 'mobx/Infra'
import type _Home from 'mobx/Home'
import type _Application from 'mobx/Application'
import { useStores } from 'hooks/useStores'

const StyledFormControlLabel = styled(FormControlLabel)`
	margin-top: 15px;
	display: flex;

	${({ $isNoteShown }) => $isNoteShown && 'align-items: flex-start;'}

	${respondAbove.md`
		margin-top: 18px;
	`}

	.MuiButtonBase-root {
		${({ $isNoteShown }) => $isNoteShown && 'padding-top: 2px;'}
	}

	.MuiIconButton-colorSecondary:hover {
		background: unset;
	}
`

const Info = styled(TypographyPro)`
	color: var(--fonts);
	max-width: 461px;
`

const Note = styled(TypographyPro)`
	color: var(--inactive);

	${respondAbove.md`
		max-width: 461px;
	`}
`

const Title = styled(TypographyPro)`
	text-transform: uppercase;
	color: var(--strokeMenuTitle);
`

const Button = styled(ButtonBase)`
	${respondAbove.md`
		width: 164px;
	`}
`

const AccountSettingsContainer = styled.div`
	color: var(--fonts);
	padding-inline-start: 20px;
	padding-inline-end: 21px;

	${respondAbove.md`
		width: 580px;
		padding: unset;
	`}
`

const StyledSection = styled(Grid)`
	display: flex;
	flex-direction: column;
	border-top: 1px solid #ecebeb;
	padding: 20px 0px;

	${respondAbove.md`
		padding: 30px 0px;
	`}

	& .MuiCheckbox-colorSecondary {
		color: #202124;
	}

	& > ${StyledFormControlLabel}:first-of-type {
		margin-top: 0px;
	}
`

const StyledFirstSection = styled(StyledSection)`
	margin-top: 30px;

	${respondAbove.md`
		margin-top: 50px;
	`}
`

const StyledButtonsGrid = styled(Grid)`
	margin-top: 40px;
	column-gap: 10px;

	${respondAbove.md`
		margin-top: 50px;
		justify-content: center;
	`}
`

const StyledDeleteAccountGrid = styled(Grid)`
	margin-top: 17px;
	justify-content: center;

	${respondAbove.md`
		margin-top: 20px;
	`}
`

const StyledGridCheckBoxLabel = styled.div``
const StyledLogo = styled.div`
	height: 35px;
	width: 120px;

	${respondAbove.md`
		margin: 0 auto;
		height: 45px;
		width: 218.55px;
	`}

	background-repeat: no-repeat;
	background-size: contain;
	background-position: center;
	background-image: ${({ $logoUrl }) => `url('${$logoUrl}')`};
	flex-grow: 1;
`

const StyledTypographyQuestion = styled(TypographyPro)`
	margin-top: 15px;

	${respondAbove.md`
		margin-bottom: 15px;
	`}
`

export const AccountSettings = inject(
	'Account',
	'User',
	'Infra',
	'Home',
	'Application'
)(
	observer(
		({
			Application,
			Account,
			Infra,
			Home,
		}: {
			Application: typeof _Application
			Account: typeof _Account
			Infra: typeof _Infra
			Home: typeof _Home
		}) => {
			const { onCommunicationPrefsChange, communicationPrefs, saveCommunicationPrefs, deleteAccount, getAccountSettings, signedOut } = Account
			const isDesktop = useMediaQuery({ query: mediaQueriesBreakpoints.lg })
			const router = useRouter()
			const { cartStore } = useStores()

			let logoUrl

			if (Infra?.appParams?.eCommerce) {
				if (isDesktop) {
					logoUrl = Infra?.appParams?.assets?.logoDesktopHeader
				} else {
					logoUrl = Infra?.appParams?.assets?.logoMobileHeader
				}
			} else {
				// this is for non-ecommerce
				logoUrl = oStorage.get('logo')
			}

			useEffect(() => {
				getAccountSettings(Application.backendChannel)
			}, [])

			const askIfUserSure = (action) => {
				Infra.setNotification({
					open: true,
					title: <StyledLogo $logoUrl={logoUrl} />,
					message: (
						<>
							<StyledTypographyQuestion variant="h2">{getTranslatedTextByKey('webviewFlow.areYouSure')}</StyledTypographyQuestion>
							{getTranslatedTextByKey('webviewFlow.signOutResetWarning')}
						</>
					),
					cancelAction: () => {
						Infra.closeNotification()
					},
					okAction: async () => {
						Infra.closeNotification()
						await action()
					},
					cancelText: getTranslatedTextByKey('cancel'),
					okText: getTranslatedTextByKey('btns.yes'),
				})
			}

			const signOrDeleteAction = async (isSignOut) => {
				Infra.setLoading(true)
				Account.setLoggingOut(true)

				try {
					cartStore.resetCart()
					cartStore.setReady(false)
					if (isSignOut) {
						await signedOut(Home.setBackdropEnabled, Home.setClickedFeaturedItem)
					} else {
						await deleteAccount(Application.backendChannel)
					}
				} catch (err: Error) {
					Account.setLoggingOut(false)
					Infra.setLoading(false)
					throw err
				}

				await router.push(`/?signoutAction=${isSignOut ? 'signout' : 'delete'}`)

				Account.setLoggingOut(false)
				Infra.setLoading(false)
			}

			return (
				<AccountSettingsContainer>
					<Title variant="h2">{getTranslatedTextByKey('eCommerce.accountSettings.accountSettings')}</Title>
					<StyledFirstSection>
						<Title variant="h4">{getTranslatedTextByKey('eCommerce.accountSettings.personalInfo')}</Title>
						<UserPersonalInfo />
					</StyledFirstSection>
					<StyledSection>
						<Title variant="h4">{getTranslatedTextByKey('eCommerce.accountSettings.communicationPrefs')}</Title>
						{/* <Info>{clubName}</Info> */}
						{/* {clubName && <Info>(member since {date})</Info>} */}
						{/* {clubName && <StyledLeave onClick={leaveClub}>Leave</StyledLeave>} */}
					</StyledSection>
					<StyledSection>
						{Object.entries(communicationPrefs)
							.filter(([, { showToUser }]) => showToUser)
							.map(([key, { label, value, note, showToUser }]) => (
								<StyledFormControlLabel
									key={key}
									control={
										<Checkbox
											onChange={() => {
												onCommunicationPrefsChange({ [key]: { value: !value, label, note, showToUser } })
											}}
											checked={value}
											name={key}
											inputProps={{
												'data-testid': 'communication',
											}}
										/>
									}
									label={
										<StyledGridCheckBoxLabel>
											<Info variant="BodyRegular">{label}</Info>
											{note && <Note variant="BodySmall">{note}</Note>}
										</StyledGridCheckBoxLabel>
									}
									$isNoteShown={!!note}
								/>
							))}
						<StyledButtonsGrid container direction="row" wrap="nowrap">
							<Button
								onClick={() => {
									askIfUserSure(() => signOrDeleteAction(true))
								}}
								$type="secondary"
								data-testid="account-sign-out-button"
							>
								{getTranslatedTextByKey('eCommerce.accountSettings.signOut')}
							</Button>
							<Button
								onClick={() => {
									saveCommunicationPrefs(Application.backendChannel)
								}}
								data-testid="account-save-button"
							>
								{getTranslatedTextByKey('eCommerce.accountSettings.save')}
							</Button>
						</StyledButtonsGrid>
						<StyledDeleteAccountGrid container direction="row">
							<CTAText
								$type="danger"
								onClick={() => askIfUserSure(() => signOrDeleteAction(false))}
								data-testid="delete-account-button"
							>
								{getTranslatedTextByKey('eCommerce.accountSettings.deleteAccount')}
							</CTAText>
						</StyledDeleteAccountGrid>
					</StyledSection>
				</AccountSettingsContainer>
			)
		}
	)
)
